import { Dispatch, ReactElement, useState } from 'react';
import { BASE_URL } from '../../../../utilities/apiConstants';
import {
  useRaptorLibraryFolders,
  useRaptorLibraryFiles,
  useRaptorLibraryAllFiles,
  RaptorLibraryResponse,
} from './services/queries';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import SearchBar from '../../../ui/inputs/SearchBar';
import { FaFolder } from "react-icons/fa6";
import { FaExternalLinkAlt } from 'react-icons/fa';
import RSCard from '../../../ui/containers/RSCard';
import type { RaptorLibraryFolderFilesIndex } from './services/queries';

/**
 * Search match: query must appear as a contiguous substring (case-insensitive).
 * So "EMIR" only matches text that actually contains "emir", not random E-M-I-R in order.
 */
function searchMatch(text: string, query: string): boolean {
  if (!query.trim()) return true;
  return text.toLowerCase().includes(query.toLowerCase().trim());
}

interface FileSelectorsProps {
  filesData?: RaptorLibraryResponse;
  selectedFolder: string;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  /** When set, search runs across all folders and files (fuzzy). */
  searchQuery?: string;
  allFilesIndex?: RaptorLibraryFolderFilesIndex[];
}

function getSortedOptions(data: RaptorLibraryResponse): string[] {
  // Check if items is an array of objects with name and order properties
  if (data.length > 0 && typeof data[0] === 'object' && 'name' in data[0]) {
    return (data as Array<{ name: string; order?: number }>)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => item.name);
  }

  return data as string[];
}

function FileSelectors(props: FileSelectorsProps): ReactElement {
  const {
    filesData,
    selectedFolder,
    isLoading,
    isFetching,
    error,
    searchQuery = '',
    allFilesIndex,
  } = props;
  const hasSelectedFolder = !!selectedFolder;
  const isBusy = isLoading || isFetching;
  const isSearchMode = searchQuery.trim() !== '' && allFilesIndex && allFilesIndex.length > 0;

  const selectedFolderFiles =
    hasSelectedFolder && filesData && filesData.length > 0
      ? getSortedOptions(filesData)
      : [];

  const searchResults: { folder: string; file: string }[] = isSearchMode
    ? (() => {
      const q = searchQuery.trim();
      const out: { folder: string; file: string }[] = [];
      for (const { folder, files } of allFilesIndex) {
        const folderMatches = searchMatch(folder, q);
        for (const file of files) {
          const fileDisplayName = file.replace(/\.pdf$/i, '');
          const fileMatches = searchMatch(fileDisplayName, q);
          if (folderMatches || fileMatches) {
            out.push({ folder, file });
          }
        }
      }
      return out;
    })()
    : [];

  const fileOptions = isSearchMode
    ? searchResults
    : selectedFolderFiles.map((file) => ({ folder: selectedFolder, file }));

  const subtitle = (() => {
    if (isSearchMode) {
      return searchResults.length === 0
        ? `No files or folders match “${searchQuery}”.`
        : `Showing ${searchResults.length} file${searchResults.length === 1 ? '' : 's'} matching “${searchQuery}”.`;
    }
    if (!hasSelectedFolder) {
      return 'Select a folder to see its files, or search above.';
    }
    if (isBusy) {
      return `Loading files for “${selectedFolder}”...`;
    }
    if (error) {
      return `Failed to load files for “${selectedFolder}”.`;
    }
    if (selectedFolderFiles.length === 0) {
      return `No files available in “${selectedFolder}”.`;
    }
    return `Showing ${selectedFolderFiles.length} file${selectedFolderFiles.length === 1 ? '' : 's'} in “${selectedFolder}”.`;
  })();

  return (
    <RSCard>
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">All Files</h2>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
      </div>

      <div className="relative overflow-hidden rounded border border-slate-200 bg-white">
        <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,1fr)] items-center gap-2 border-b bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <span>Name</span>
          <span className="text-right">Action</span>
        </div>

        <div className="divide-y divide-slate-100">
          {!hasSelectedFolder && !isSearchMode && (
            <div className="px-4 py-3 text-sm text-slate-500">
              Choose a folder above to view its files, or search to find files across all folders.
            </div>
          )}

          {(hasSelectedFolder || isSearchMode) && isBusy && !isSearchMode && (
            <>
              {Array.from({ length: 3 }).map((_, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[minmax(0,3fr)_minmax(0,1fr)] items-center gap-2 px-4 py-2 text-sm"
                >
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className="h-8 w-8 rounded bg-slate-200 animate-pulse" />
                    <div className="h-3 w-1/3 rounded bg-slate-200 animate-pulse" />
                  </div>
                  <div className="flex justify-end">
                    <div className="h-3 w-10 rounded bg-slate-200 animate-pulse" />
                  </div>
                </div>
              ))}
            </>
          )}

          {!isBusy && !error && fileOptions.length === 0 && (hasSelectedFolder || isSearchMode) && (
            <div className="px-4 py-3 text-sm text-slate-500">
              No files to display.
            </div>
          )}

          {!isBusy &&
            !error &&
            fileOptions.map(({ folder, file }) => {
              const readableTitle = file.replace(/\.pdf$/i, '');
              const href = `${BASE_URL}raptor_library/${folder}/${file}`;
              const rowKey = `${folder}/${file}`;

              return (
                <a
                  key={rowKey}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="grid grid-cols-[minmax(0,3fr)_minmax(0,1fr)] items-center gap-2 px-4 py-2 text-sm transition hover:bg-slate-50"
                >
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-red-100 text-xs font-bold text-red-600">
                      PDF
                    </div>
                    <div className="min-w-0">
                      <span className="truncate block">{readableTitle}</span>
                      {isSearchMode && (
                        <span className="text-xs text-slate-400 truncate block">{folder}</span>
                      )}
                    </div>
                  </div>
                  <span className="flex items-center justify-end text-mainblue">
                    <FaExternalLinkAlt className="h-3 w-3" aria-hidden />
                    <span className="sr-only">Open in new tab</span>
                  </span>
                </a>
              );
            })}

          {hasSelectedFolder && !isSearchMode && !isBusy && !!error && (
            <div className="px-4 py-3 text-sm text-red-600">
              Failed to load files. Please try again.
            </div>
          )}
        </div>
      </div>
    </RSCard>
  );
}

interface FolderSelectorsProps {
  foldersData: RaptorLibraryResponse;
  setSelectedFolder: Dispatch<any>;
  selectedFolder: string;
  folderSearchQuery: string;
  setFolderSearchQuery: (value: string) => void;
  /** When provided with folderSearchQuery, folders that contain matching files are also shown. */
  allFilesIndex?: RaptorLibraryFolderFilesIndex[];
}

function FolderSelectors(props: FolderSelectorsProps): ReactElement {
  const {
    foldersData,
    setSelectedFolder,
    selectedFolder,
    folderSearchQuery,
    setFolderSearchQuery,
    allFilesIndex,
  } = props;
  const allFolders = getSortedOptions(foldersData);
  const query = folderSearchQuery.trim();

  const folderOptions = (() => {
    if (!query) return allFolders;
    const nameMatches = allFolders.filter((name) => searchMatch(name, query));
    if (!allFilesIndex || allFilesIndex.length === 0) return nameMatches;
    const foldersWithMatchingFiles = new Set<string>();
    for (const { folder, files } of allFilesIndex) {
      const folderMatches = searchMatch(folder, query);
      if (folderMatches) foldersWithMatchingFiles.add(folder);
      for (const file of files) {
        if (searchMatch(file.replace(/\.pdf$/i, ''), query)) {
          foldersWithMatchingFiles.add(folder);
        }
      }
    }
    const combined = new Set([...nameMatches, ...foldersWithMatchingFiles]);
    return allFolders.filter((name) => combined.has(name));
  })();

  return (
    <RSCard>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <p className="text-lg text-slate-500">
          Browse folders and open files from the Raptor library.
        </p>
        <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center">
          <SearchBar
            placeholder="Search folders and files..."
            searchHandler={setFolderSearchQuery}
            debounceTime={200}
            className='w-56'
          />
        </div>
      </div>

      <div className="my-4 h-px bg-slate-200" />

      <div className="mb-4 mt-6 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Folders</h2>
          <p className="mt-1 text-sm text-slate-500">
            Select a library section to see its files.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {folderOptions.length === 0 && folderSearchQuery.trim() ? (
          <p className="col-span-full py-4 text-sm text-slate-500">
            No folders match “{folderSearchQuery}”.
          </p>
        ) : null}
        {folderOptions.map((el) => {
          const isActive = el === selectedFolder;

          return (
            <button
              key={el}
              type="button"
              onClick={() => setSelectedFolder(el)}
              className={`flex h-16 w-full items-center gap-3 rounded border bg-white px-3 py-2 text-left text-sm shadow-sm transition ${isActive
                ? 'border-mainblue ring-2 ring-mainblue'
                : 'border-slate-200 hover:border-mainblue/60 hover:shadow-md'
                }`}
            >
              <FaFolder className="text-yellow-500 text-3xl" />
              <span className="font-semibold text-slate-800">{el}</span>
            </button>
          );
        })}
      </div>
    </RSCard>
  );
}

function Library(): ReactElement {
  const [selectedFolder, setSelectedFolder] = useState('');
  const [folderSearchQuery, setFolderSearchQuery] = useState('');
  const {
    data: foldersData,
    isLoading: foldersLoading,
    isFetching: foldersFetching,
    error: foldersError,
  } = useRaptorLibraryFolders();

  const {
    data: filesData,
    isLoading: filesLoading,
    isFetching: filesFetching,
    error: filesError,
  } = useRaptorLibraryFiles(selectedFolder);

  const { data: allFilesIndex } = useRaptorLibraryAllFiles(foldersData);

  return (
    <QueryBoundary
      isLoading={foldersLoading}
      isFetching={foldersFetching}
      isError={!!foldersError}
      data={foldersData}
      loadingMessages={['Loading library folders...']}
      errorMessage="Failed to load library folders"
      noDataMessage="No library folders available"
    >
      {(folders) => (
        <div className="mx-auto flex w-full flex-col gap-6 px-4 py-3">
          <FolderSelectors
            foldersData={folders}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
            folderSearchQuery={folderSearchQuery}
            setFolderSearchQuery={setFolderSearchQuery}
            allFilesIndex={allFilesIndex}
          />

          <div>
            <FileSelectors
              filesData={filesData}
              selectedFolder={selectedFolder}
              isLoading={filesLoading}
              isFetching={filesFetching}
              error={filesError}
              searchQuery={folderSearchQuery}
              allFilesIndex={allFilesIndex}
            />
          </div>
        </div>
      )}
    </QueryBoundary>
  );
}

export default Library;
